import type { ISumbitPassword } from '@/api/user';

import { defineStore } from 'pinia';
import WaterMark from 'l-watermark';

import { store } from '@/store';
import router from '@/router';

import { UserApiSerivce, IUserData } from '@/api/user';
import {
  getOAToken,
  getFPLToken,
  getToken,
  removeToken,
  removeOACookie,
  removeFPLToken,
} from '@/utils/auth';
import { usePermissionStore } from '@/store/modules/permission';

interface UserState {
  tempToken: string;
  userName: string;
  userInfo: UserInfo | null;
  applicationPermission: string[];
  visitorTrackerInstance: any;
}

export interface UserInfo {
  accountId: string;
  accountName: string;
  applicationId: string;
  userName: string;
  roles?: string[];
  permissions: string[];
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    tempToken: '',
    userName: '',
    userInfo: null,
    applicationPermission: [],
    visitorTrackerInstance: null,
  }),
  getters: {
    getTempToken(): string {
      return this.tempToken;
    },
    getUserName(): string {
      return this.userName;
    },
    getUserInfo(): UserInfo | null {
      return this.userInfo;
    },
    getApplicationPermission(): string[] {
      return this.applicationPermission;
    },
    getVisitorTrackerInstance(): any {
      return this.visitorTrackerInstance;
    },
  },
  actions: {
    setTempToken(value: string): void {
      this.tempToken = value;
    },
    setUserName(value: string): void {
      this.userName = value;
    },
    setUserInfo(info: UserInfo): void {
      this.userInfo = info;
    },
    setApplicationPermission(value: string[]): void {
      this.applicationPermission = value;
    },
    resetState(): void {
      this.userInfo = null;

      window.waterMark1?.remove();

      removeToken();
    },
    getPasswordPolicy(): Promise<{ regexp: string; regexpTips: string }> {
      return new Promise((resolve, reject) => {
        UserApiSerivce.getPasswordPolicy()
          .then((res) => {
            resolve(res[0]);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    checkPassword(userInfo: IUserData): Promise<{ warningType: number }> {
      return new Promise((resolve) => {
        UserApiSerivce.checkPassword(userInfo).then((data) => {
          resolve(data);
        });
      });
    },
    changePassword(data: ISumbitPassword): Promise<void> {
      return new Promise((resolve, reject) => {
        UserApiSerivce.changePassword(data)
          .then((res) => {
            resolve(res);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    getPermission() {
      return new Promise((resolve, reject) => {
        const permissionStore = usePermissionStore();
        UserApiSerivce.getPermission()
          .then((data) => {
            this.setUserName(data.userName);
            this.setUserInfo({
              accountId: data.accountId,
              accountName: data.accountName,
              applicationId: data.applicationId,
              userName: data.userName,
              roles: [],
              permissions: data.behaviors,
            });

            try {
              const tracker = this.visitorTrackerInstance;
              tracker.setUserInfo({
                no: data.userName,
              });
            } catch (error) {
              console.error('tracker页面追踪 用户信息配置失败', error);
            }
            const userText = `${data.accountName} ${data.userName}`;

            window.waterMark1 = WaterMark.page({
              target: document.body,
              text: userText,
              color: 'rgba(0, 0, 0, 0.05)',
              fontSize: 14,
              cSpace: 50,
              vSpace: 50,
              // mixBlendMode: "multiply",
              // filter: "brightness(4.5)"
            });

            UserApiSerivce.getUserInformation().then((data) => {
              const { applicationCodes } = data;
              this.setApplicationPermission(applicationCodes);
            });

            permissionStore.setPermissionCodeList(data.behaviors);
            permissionStore.changePermissionCode();
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    login(
      userData: IUserData,
      targetUrl?: string
    ): Promise<{ token?: string; redirectUrl: string }> {
      return new Promise((resolve, reject) => {
        UserApiSerivce.login(userData, targetUrl)
          .then((res) => {
            this.setTempToken(res.token);
            resolve(res);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    fplLogin(userData: IUserData): Promise<string> {
      return new Promise((resolve, reject) => {
        UserApiSerivce.fplLogin(userData)
          .then((res) => {
            resolve(res);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    get4PLTokenWithOAToken(): Promise<string> {
      return new Promise((resolve, reject) => {
        if (!getOAToken()) {
          reject('OAToken 为空');
          return;
        }
        this.resetState();

        // 使用Promise.all确保两个接口都成功完成
        Promise.all([
          UserApiSerivce.getFPLTokenWithOAToken({
            lsmyToken: getOAToken() || '',
          }),
          UserApiSerivce.get4PLTokenWithOAToken({
            lsmyToken: getOAToken() || '',
          }),
        ])
          .then(() => {
            // 验证token是否正确获取
            if (getToken() && getFPLToken()) {
              resolve('获取4PL token 成功');
            } else {
              reject('获取4PL token 失败');
            }
          })
          .catch((error) => {
            console.error('获取token失败:', error);
            reject(error);
          });
      });
    },
    logout(): Promise<void> {
      removeOACookie();
      removeFPLToken();
      return new Promise((resolve, reject) => {
        UserApiSerivce.logout()
          .then(() => {
            this.resetState();
            router.push({ path: '/login' });
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    setVisitorTrackerInstance(instance: any) {
      this.visitorTrackerInstance = instance;
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWidthOut() {
  return useUserStore(store);
}
